import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetTagArticlesQuery } from '@/services/newsApi';
import { useHaptics } from '@/hooks/useHaptics';

interface Props {
  tagSlug: string;
  currentArticleSlug: string;
  locale?: string;
}

export const RelatedArticles = memo(function RelatedArticles({
  tagSlug,
  currentArticleSlug,
  locale = 'en',
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation('news');
  const haptics = useHaptics();

  const { data: result } = useGetTagArticlesQuery(
    { slug: tagSlug, limit: 5, locale, sort: 'trending' },
    { skip: !tagSlug },
  );

  const related = result?.docs
    .filter((a) => a.slug !== currentArticleSlug)
    .slice(0, 4);

  if (!related || related.length === 0) return null;

  return (
    <section className="pb-3 pt-4">
      <h2 className="mb-3.5 px-5 text-lg font-bold text-fg">
        {t('section.related', { defaultValue: 'Related Articles' })}
      </h2>
      <div className="no-scrollbar overflow-x-auto">
        <div className="flex gap-3 px-5">
          {related.map((item) => {
            const localized = item.content.find((c) => c.locale === locale) ?? item.content[0];
            if (!localized) return null;
            return (
              <button
                key={item._id}
                type="button"
                onClick={() => {
                  haptics.selection();
                  navigate(`/news/${item.slug}`);
                }}
                className="w-[180px] shrink-0 overflow-hidden rounded-xl bg-bg-secondary text-left transition-opacity active:opacity-80"
              >
                {item.heroImage ? (
                  <img
                    src={item.heroImage.thumbnailUrl || item.heroImage.url}
                    alt={item.heroImage.alt}
                    loading="lazy"
                    className="h-[100px] w-[180px] object-cover"
                  />
                ) : (
                  <div className="h-[100px] w-[180px] bg-border" />
                )}
                <h3 className="line-clamp-2 px-2.5 pb-1 pt-2.5 text-[13px] font-semibold leading-[18px] text-fg">
                  {localized.title}
                </h3>
                <p className="px-2.5 pb-2.5 text-[11px] text-[#6a6a6a]">
                  {item.readingTimeMinutes} min read
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
});
